import { useRef } from 'react'

import type {
  PrimaryActionsDefinitionFn,
  SecondaryActionsDefinition
} from '@factorialco/f0-react/dist/experimental'
import {
  EmployeeEstimatedPeriodBaseSalaryPaginatedDocument,
  EmployeeEstimatedPeriodBaseSalaryPaginatedQuery
} from 'generated/resources/graphql'
import i18n from 'modules/core/lib/i18n'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useEmployeeFilters } from 'modules/employees/lib/filters'

const SUPPLEMENTS_COLLECTION_PER_PAGE = 25

export type SupplementSalaryRecord = NonNullable<
  NonNullable<
    EmployeeEstimatedPeriodBaseSalaryPaginatedQuery['payroll']['employeeEstimatedPeriodBaseSalariesConnection']
  >['nodes']
>[number]

export interface SupplementsSalariesDataCollectionSourceOptions {
  policyPeriodId: number | null | undefined
  primaryActions?: PrimaryActionsDefinitionFn
  secondaryActions?: SecondaryActionsDefinition
}

/**
 * DataCollection source for the Supplements page that uses
 * employeeEstimatedPeriodBaseSalariesConnection as its data source.
 *
 * This ensures pagination count matches the rendered rows since the backend
 * filters by policy period ID and all employee filters (team, location, legal entity, etc.).
 *
 * The returned records include both salary data and employee information,
 * which can be used directly for rendering without additional lookups.
 */
export function useSupplementsSalariesDataCollectionSource({
  policyPeriodId,
  primaryActions,
  secondaryActions
}: SupplementsSalariesDataCollectionSourceOptions) {
  const { filters: employeeFilters, hasJobCatalogTreeFeature } = useEmployeeFilters()
  const employeeFiltersRef = useRef(employeeFilters)
  employeeFiltersRef.current = employeeFilters

  return useGraphqlDataCollectionSource(
    {
      document: EmployeeEstimatedPeriodBaseSalaryPaginatedDocument,
      perPage: SUPPLEMENTS_COLLECTION_PER_PAGE,
      paginationType: 'pages',
      mapVariables: ({ filters, search, sortings }) => {
        const teamIds = Array.isArray(filters?.team) ? filters.team : undefined
        const locationIds = Array.isArray(filters?.workplace) ? filters.workplace : undefined
        const legalEntityIds = Array.isArray(filters?.legalEntity) ? filters.legalEntity : undefined
        const jobRoleRaw = Array.isArray(filters?.jobRole) ? filters.jobRole : undefined
        const jobLevelIds =
          !hasJobCatalogTreeFeature && jobRoleRaw?.length
            ? jobRoleRaw.map((id: string | number) => Number(id))
            : undefined
        const jobCatalogTreeNodeIds =
          hasJobCatalogTreeFeature && jobRoleRaw?.length
            ? jobRoleRaw.map((id: string | number) => id.toString())
            : undefined
        const reportingTo = Array.isArray(filters?.reportingTo) ? filters.reportingTo : undefined
        const timeoffManagerIds = Array.isArray(filters?.timeoffManager)
          ? filters.timeoffManager
          : undefined
        const accessStatus = Array.isArray(filters?.accessStatus) ? filters.accessStatus : undefined
        const accessStatusWithDefault = accessStatus?.length
          ? accessStatus
          : ['not_invited', 'accepted', 'active', 'invited']

        return {
          policyPeriodId: policyPeriodId ?? 0,
          search: search || undefined,
          teamIds: teamIds?.length ? teamIds : undefined,
          locationIds: locationIds?.length ? locationIds : undefined,
          legalEntityIds: legalEntityIds?.length ? legalEntityIds : undefined,
          referenceContractLevelIds: jobLevelIds?.length ? jobLevelIds : undefined,
          referenceContractTreeLevelUuids: jobCatalogTreeNodeIds?.length
            ? jobCatalogTreeNodeIds
            : undefined,
          managerIds: reportingTo?.length ? reportingTo : undefined,
          timeoffManagerIds: timeoffManagerIds?.length ? timeoffManagerIds : undefined,
          accessStatus: accessStatusWithDefault,
          sortOrder: sortings?.[0]
        }
      },
      mapPagination: (data) => ({
        total: data?.payroll?.employeeEstimatedPeriodBaseSalariesConnection?.totalCount ?? 0
      }),
      mapData: (data) => data?.payroll?.employeeEstimatedPeriodBaseSalariesConnection?.nodes ?? [],
      itemUrl: () => '',
      sortings: {
        employee_full_name: { label: i18n.t('common.name') }
      },
      defaultSortings: { field: 'employee_full_name', order: 'asc' as const },
      search: { enabled: true },
      filters: {
        workplace: employeeFiltersRef.current?.workplace,
        team: employeeFiltersRef.current?.team,
        legalEntity: employeeFiltersRef.current?.legalEntity,
        jobRole: employeeFiltersRef.current?.jobRole,
        accessStatus: employeeFiltersRef.current?.accessStatus,
        reportingTo: employeeFiltersRef.current?.reportingTo,
        timeoffManager: employeeFiltersRef.current?.timeoffManager
      },
      primaryActions,
      secondaryActions
    },
    [policyPeriodId, hasJobCatalogTreeFeature, primaryActions, secondaryActions]
  )
}
